import i18nFactory from '@yandex-int/i18n';
import { differenceWith, isEmpty, isEqual } from 'lodash';

import { TimeTargetingDay } from '../../typings/common';
import {
    ChangedOptions,
    TimeTargetCoefficientsDiff,
    TimeTargetingCorrection,
    TimeTargetingDayCorrectionsDisplay,
    TimeTargetingDayDisplay,
    TimeTargetingHours
} from '../../typings/log';
import { LogHelpers } from '../LogHelpers/LogHelpers';

import * as keyset from './TimeTargetingHelpers.i18n';

const i18n = i18nFactory(keyset);

const lastHourIndex = 23,
    lastHour = 24,
    possibleWeekDays = 7,
    TimeTargetingOff = 0,
    TimeTargetingOn = 100;

type CommonHours = TimeTargetingHours | TimeTargetingCorrection;
interface TimeRange {
    start: number;
    end?: number;
    oldCoef?: number;
    newCoef?: number;
}

interface TimeRangeAccumulator {
    prev?: TimeTargetCoefficientsDiff;
    ranges: Array<TimeRange>;
    currentRange: TimeRange;
}

export class TimeTargetingHelpers {
    private static timeSeparator = '-';

    /**
     * Метод получения списка дней с корректировками
     * Получает на вход мэп с массивом корректировок для каждого дня
     * Возвращает массив объектов "день/дни (локализованная строка)" + "корректировки"
     * Идея в том, что если у нескольких дней корректировки одинаковые,
     * то метод сводит их в 1 объект с полем daysText вида "ПН-ПТ"
     * @param {Map<TimeTargetingDay, Array<TimeTargetingCorrection>>} days - массив с корректировками на 7 элементов
     * @returns {Array<TimeTargetingDayCorrectionsDisplay>}
     */
    static getCorrectionsDayText = (
        days: Map<TimeTargetingDay, Array<TimeTargetingCorrection>>
    ): Array<TimeTargetingDayCorrectionsDisplay> => {
        const pushCallback = (
            result: Array<TimeTargetingDayCorrectionsDisplay>,
            daysStr: string,
            currentDay: Array<TimeTargetingCorrection>
        ): void => {
            result.push({
                daysText: daysStr,
                corrections: currentDay
            });
        };

        return TimeTargetingHelpers.createDayArray<TimeTargetingDayCorrectionsDisplay>(days, pushCallback);
    };

    /**
     * Метод получения списка дней с часами, когда временной таргетинг включен
     * Получает на вход мэп с массивом включенных часов для каждого дня
     * Возвращает массив объектов "день/дни (локализованная строка)" + "включенные часы"
     * Идея в том, что если у нескольких дней часы настроены одинаково,
     * то метод сводит их в 1 объект с полем daysText вида "ПН-ПТ"
     * @param {Map<TimeTargetingDay, Array<TimeTargetingHours>>} days - массив с часами на 7 элементов
     * @returns {Array<TimeTargetingDayDisplay>}
     */
    static getTimeDayText = (
        days: Map<TimeTargetingDay, Array<TimeTargetingHours>>
    ): Array<TimeTargetingDayDisplay> => {
        const pushCallback = (
            result: Array<TimeTargetingDayDisplay>,
            daysStr: string,
            currentDay: Array<TimeTargetingHours>
        ): void => {
            result.push({
                daysText: daysStr,
                hours: currentDay
            });
        };

        return TimeTargetingHelpers.createDayArray<TimeTargetingDayDisplay>(days, pushCallback);
    };

    /**
     * Метод расчета часов
     * На вход получает массив пар новое значение ТТ - старое значение ТТ
     * На выходе получает массив  для времени показа
     * Мы считаем, что ТТ может быть или выключенным (0) или включенным (!0)
     * @param {Array<TimeTargetCoefficientsDiff>} hours
     * @param {Boolean} isNew - флаг, что ТТ выставляется впервые
     * @returns Array<TimeTargetingHours>
     */
    static getTimeRange = (hours: Array<TimeTargetCoefficientsDiff>, isNew: boolean): Array<TimeTargetingHours> => {
        const mapRange = (value: TimeRange): TimeTargetingHours => {
            return {
                hoursText: TimeTargetingHelpers.getHoursIntervalText(value.start, value.end),
                timeChangedType: TimeTargetingHelpers.getChangedOptionForTime(value.newCoef, value.oldCoef)
            };
        };
        return TimeTargetingHelpers.calcRanges<TimeTargetingHours>(
            hours,
            TimeTargetingHelpers.isHoursChanged,
            TimeTargetingHelpers.isHourOn,
            mapRange,
            isNew
        );
    };

    /**
     * Метод расчета корректировок
     * На вход получает массив пар новое значение ТТ - старое значение ТТ
     * На выходе получает массив для корректировок
     * Мы считаем корректировка либо есть (!0 и !100), либо нет (0 и 100)
     * @param {Array<TimeTargetCoefficientsDiff>} hours
     * @param {Boolean} isNew - флаг, что ТТ выставляется впервые
     * @returns Array<TimeTargetingCorrection>
     */
    static getCorrectionRange = (
        hours: Array<TimeTargetCoefficientsDiff>,
        isNew: boolean
    ): Array<TimeTargetingCorrection> => {
        const mapRange = (value: TimeRange): TimeTargetingCorrection => {
            return {
                oldCorrection: value.oldCoef || TimeTargetingOff,
                correction: value.newCoef || TimeTargetingOff,
                correctionChangedType: TimeTargetingHelpers.getChangedOptionForCorrection(value.newCoef, value.oldCoef),
                hoursText: TimeTargetingHelpers.getHoursIntervalText(value.start, value.end)
            };
        };

        return TimeTargetingHelpers.calcRanges<TimeTargetingCorrection>(
            hours,
            TimeTargetingHelpers.isCorrectionsChanged,
            TimeTargetingHelpers.isCorrectionOn,
            mapRange,
            isNew
        );
    };

    static getHolidaysCorrections = (
        holidays: Array<TimeTargetingCorrection>
    ): {
        newCorrection: number;
        oldCorrection: number;
        correctionChangeType: ChangedOptions;
    } => {
        const values = holidays.reduce(
            (res: { newCorrection: number; oldCorrection: number }, entry: TimeTargetingCorrection) => {
                if (entry.correction !== 0 && entry.correction !== LogHelpers.noCorrectionValue) {
                    res.newCorrection = entry.correction;
                }
                if (entry.oldCorrection !== 0 && entry.oldCorrection !== LogHelpers.noCorrectionValue) {
                    res.oldCorrection = entry.oldCorrection;
                }
                return res;
            },
            {
                newCorrection: LogHelpers.noCorrectionValue,
                oldCorrection: LogHelpers.noCorrectionValue
            }
        );
        return {
            ...values,
            correctionChangeType: TimeTargetingHelpers.getChangedOptionForCorrection(
                values.newCorrection,
                values.oldCorrection
            )
        };
    };

    /**
     * Метод расчет интервалов
     * @param {Array<TimeTargetCoefficientsDiff>} hours - входной массив часов
     * @param {(newValue: TimeTargetCoefficientsDiff, oldValue: TimeTargetCoefficientsDiff) => boolean} changeDetector - метод сравнения значений (изменилось или нет)
     * @param {(newValue: TimeTargetCoefficientsDiff) => boolean} onDetector - метод проверки, что корректировка не выключена
     * @param {(value: TimeRange) => T} mapRange - маппер на финальное значение
     * @param {Boolean} isNew - флаг, что ТТ выставляется впервые
     * @returns {Array<T>}
     */
    private static calcRanges<T>(
        hours: Array<TimeTargetCoefficientsDiff>,
        changeDetector: (newValue: TimeTargetCoefficientsDiff, oldValue: TimeTargetCoefficientsDiff) => boolean,
        onDetector: (value: TimeTargetCoefficientsDiff, isNew: boolean) => boolean,
        mapRange: (value: TimeRange) => T,
        isNew: boolean
    ): Array<T> {
        const res = hours.reduce(
            (accumulator: TimeRangeAccumulator, next: TimeTargetCoefficientsDiff, index: number) => {
                const { prev, ranges, currentRange } = accumulator,
                    isOn = onDetector(next, isNew),
                    isLastHour = index === lastHourIndex;

                let periodStarted = currentRange.start !== -1;
                let isChanged = false;

                if (prev) {
                    isChanged = changeDetector(prev, next);

                    if (
                        periodStarted && // и есть начало периода
                        (isChanged || // и либо новое значение отлично от предыдущего
                        !isOn || // либо если выключили показы
                            isLastHour) // либо если дошли до конца дня
                    ) {
                        ranges.push({
                            ...currentRange,
                            // если мы закончили _только_ из-за того, что дошли до конца - то
                            // последний индекс не 23, а 24
                            end: isLastHour && isOn && !isChanged ? lastHour : index,
                            oldCoef: prev.oldCoef,
                            newCoef: prev.newCoef
                        });

                        // обнуляем период
                        periodStarted = false;
                    }
                }

                if (!periodStarted) {
                    accumulator.currentRange = {
                        start: isOn ? index : -1
                    };
                    // если изменился последний час - добавляем его в список
                    if (isLastHour && isOn && isChanged) {
                        ranges.push({
                            start: lastHourIndex,
                            end: lastHour,
                            oldCoef: next.oldCoef,
                            newCoef: next.newCoef
                        });
                    }
                }

                accumulator.prev = next;
                return accumulator;
            },
            { prev: undefined, ranges: [], currentRange: { start: -1 } }
        );

        return res.ranges.map(mapRange);
    }

    /**
     * Метод "схлопа" дней при одинаковых часах или корректировках
     * @param {Map<TimeTargetingDay, Array<CommonHours>>} days - исходный массив из 7 дней
     * @param {(result: Array<T>, daysStr: string, currentDay: Array<CommonHours>) => void} pushCallback - коллбэк,
     * который вызывается, если дни "схлопнулись"
     * @returns {Array<T>}
     */
    private static createDayArray<T>(
        days: Map<TimeTargetingDay, Array<CommonHours>>,
        pushCallback: (result: Array<T>, daysStr: string, currentDay: Array<CommonHours>) => void
    ): Array<T> {
        let periodStart, periodEnd, daysStr;

        const result: Array<T> = [];
        const weekdaysArray = Object.keys(TimeTargetingDay);

        for (let i = 0; i < possibleWeekDays; i++) {
            const weekday = weekdaysArray[i];

            // текущий день
            const currentDay = days.get(weekday as TimeTargetingDay);
            // следующий день
            const nextDay = days.get(weekdaysArray[i + 1] as TimeTargetingDay);

            if (currentDay && currentDay.length > 0) {
                // начинаем последовательность
                if (periodStart === undefined) {
                    periodStart = weekday as TimeTargetingDay;
                }

                // если есть следующий день, но часы у них не совпали
                if (nextDay && !TimeTargetingHelpers.isHoursEqual(currentDay, nextDay)) {
                    periodEnd = weekday;
                    daysStr = TimeTargetingHelpers.getTextDayRange(
                        periodStart as TimeTargetingDay,
                        periodEnd as TimeTargetingDay
                    );
                    // кладем данные в массив результата
                    pushCallback(result, daysStr, currentDay);
                    // обнуляем начало периода
                    periodStart = undefined;
                    // если дошли до воскресенья, тоже заканчиваем период
                } else if (weekday === TimeTargetingDay.sunday) {
                    periodEnd = weekday;
                    daysStr = TimeTargetingHelpers.getTextDayRange(
                        periodStart as TimeTargetingDay,
                        periodEnd as TimeTargetingDay
                    );
                    pushCallback(result, daysStr, currentDay);
                    periodStart = undefined;
                }
            }
        }
        return result;
    }

    /**
     * Метод сравнения массивов часов или корректировок
     * @param {Array<CommonHours>} a
     * @param {Array<CommonHours>} b
     * @returns {boolean}
     */
    private static isHoursEqual(a: Array<CommonHours>, b: Array<CommonHours>): boolean {
        return isEmpty(differenceWith(a, b, isEqual));
    }

    /**
     * Сравнивает старые и новые часы с текущими (по критерию включены/выключены)
     * @param {TimeTargetCoefficientsDiff} prev - начало периода
     * @param {TimeTargetCoefficientsDiff} next - текущий час
     * @returns {boolean}
     */
    private static isHoursChanged(prev: TimeTargetCoefficientsDiff, next: TimeTargetCoefficientsDiff): boolean {
        const currentIsOff = prev.newCoef === TimeTargetingOff,
            currentWasOff = prev.oldCoef === TimeTargetingOff,
            newIsOff = next.newCoef === TimeTargetingOff,
            newWasOff = next.oldCoef === TimeTargetingOff;

        return currentIsOff !== newIsOff || currentWasOff !== newWasOff;
    }

    private static isHourOn(value: TimeTargetCoefficientsDiff, isNew: boolean): boolean {
        return value.newCoef !== TimeTargetingOff || (value.oldCoef !== TimeTargetingOff && !isNew);
    }

    private static isCorrectionOn(value: TimeTargetCoefficientsDiff, isNew: boolean): boolean {
        const { newCoef, oldCoef } = value;
        return (
            (newCoef !== TimeTargetingOff && newCoef !== TimeTargetingOn) ||
            (oldCoef !== TimeTargetingOff && oldCoef !== TimeTargetingOn && !isNew)
        );
    }

    /**
     * Сравнивает старые и новые корректировки с текущими (по критерию есть/нет)
     * @param {TimeTargetCoefficientsDiff} prev - начало периода
     * @param {TimeTargetCoefficientsDiff} next - текущий час
     * @returns {boolean}
     */
    private static isCorrectionsChanged(prev: TimeTargetCoefficientsDiff, next: TimeTargetCoefficientsDiff): boolean {
        return prev.newCoef !== next.newCoef || prev.oldCoef !== next.oldCoef;
    }

    /**
     * Метод, который рассчитывает, как именно изменилась корректировка
     * Варианты: включили ТТ (ADDED), выключили ТТ (REMOVED), не изменилось (NOT_MODIFIED)
     * @param {number} newValue - новое значение
     * @param {number} oldValue - старое значение
     * @returns {ChangedOptions}
     */
    private static getChangedOptionForTime = (
        newValue: number = TimeTargetingOff,
        oldValue: number = TimeTargetingOff
    ): ChangedOptions => {
        return newValue === oldValue
            ? ChangedOptions.notModified
            : newValue === TimeTargetingOff
            ? ChangedOptions.removed
            : oldValue === TimeTargetingOff
            ? ChangedOptions.added
            : ChangedOptions.notModified;
    };

    /**
     * Метод, который рассчитывает, как именно изменилась корректировка
     * Варианты: включили ТТ (ADDED), выключили ТТ (REMOVED), не изменилось (NOT_MODIFIED), изменилась (MODIFIED)
     * причем изменение на 0 или 100 - это выключение, с 0 или 100 - включение
     * а изменение - это с 15 на 25, например
     * @param {number} newValue - новое значение
     * @param {number} oldValue - старое значение
     * @returns {ChangedOptions}
     */
    private static getChangedOptionForCorrection = (
        newValue: number = TimeTargetingOff,
        oldValue: number = TimeTargetingOff
    ): ChangedOptions => {
        return newValue === oldValue
            ? ChangedOptions.notModified
            : newValue === TimeTargetingOff || newValue === TimeTargetingOn
            ? ChangedOptions.removed
            : oldValue === TimeTargetingOff || oldValue === TimeTargetingOn
            ? ChangedOptions.added
            : ChangedOptions.modified;
    };

    /**
     * Получение строки с часом
     * @param {number} time
     * @returns {string}
     */
    private static getTime(time: number): string {
        return time < 10 ? `0${time}` : time.toString();
    }

    /**
     * Получение строки с интервалами часов
     * @param {number} start - начало периода
     * @param {number} finish - конец периода
     * @returns {string}
     */
    private static getHoursIntervalText = (start: number, finish: number = lastHour): string => {
        if (start === 0 && finish === lastHour) {
            return i18n('all_day');
        }
        return `${TimeTargetingHelpers.getTime(start)}${
            TimeTargetingHelpers.timeSeparator
        }${TimeTargetingHelpers.getTime(finish)}`;
    };

    /**
     * Получение строки с интервалами дней
     * @param {TimeTargetingDay} from - начало периода
     * @param {TimeTargetingDay} to - конец периода
     * @returns {string}
     */
    private static getTextDayRange = (from: TimeTargetingDay, to: TimeTargetingDay): string => {
        if (from === TimeTargetingDay.monday && to === TimeTargetingDay.friday) {
            return i18n('working_days');
        }
        if (from === TimeTargetingDay.saturday && to === TimeTargetingDay.sunday) {
            return i18n('weekends');
        }
        return from === to ? i18n(from) : `${i18n(from)}${TimeTargetingHelpers.timeSeparator}${i18n(to)}`;
    };
}
